import React from "react";
import { Typography } from "antd";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { convertFahrenheitToCelsius, convertFahrenheitToCelsiusOffset } from "src/utils/generalUtils";
import { formatNumber } from "src/utils/formatting";

const { Text } = Typography;

interface PlenumTempInfoContentProps {
    binDto: PremierPlusBinDTO | null | undefined;
}

interface SensorLine {
    key: string;
    label: string;
    uncalibratedF: number | null | undefined;
}

const PlenumTempInfoContent: React.FC<PlenumTempInfoContentProps> = ({ binDto }) => {
    const useMetricSystem = binDto?.platformSettings?.useMetricSystem ?? false;
    const calibrationF = binDto?.settings?.dryerSettings?.plenumTempCalibrationF ?? 0;
    const binState = binDto?.binState;
    const dryingRunning = binDto?.dryingOperation?.isRunning === true;

    const formatTemp = (tempF: number | null | undefined) => {
        if (tempF == null) return "--";
        if (!useMetricSystem) {
            return formatNumber(tempF, { decimalPlaces: 1, suffix: "°F" });
        }
        const tempC = convertFahrenheitToCelsius(tempF);
        return tempC == null ? "--" : formatNumber(tempC, { decimalPlaces: 1, suffix: "°C" });
    };

    const formatOffset = (offsetF: number | null | undefined) => {
        if (offsetF == null) return "--";
        if (!useMetricSystem) {
            return formatNumber(offsetF, { decimalPlaces: 1, suffix: "°F" });
        }
        const offsetC = convertFahrenheitToCelsiusOffset(offsetF);
        return offsetC == null ? "--" : formatNumber(offsetC, { decimalPlaces: 1, suffix: "°C" });
    };

    const getCalibratedTempDisplay = (uncalibratedF: number | null | undefined) => {
        if (uncalibratedF == null) return "--";
        return formatTemp(uncalibratedF + calibrationF);
    };

    const avgUncalibratedF = binState?.averagePlenumRhtSensorState?.temperatureF
        ?? binDto?.averagePlenumRhtSensor_TemperatureF
        ?? null;
    const avgCalibratedF = avgUncalibratedF == null ? null : avgUncalibratedF + calibrationF;

    const aux: SensorLine = {
        key: "aux",
        label: "Aux",
        uncalibratedF: binState?.auxPlenum?.temperatureF ?? null,
    };
    const fans: SensorLine[] = (binState?.fanStates ?? []).map((fan) => ({
        key: `fan-${fan.number}`,
        label: `Fan ${fan.number}`,
        uncalibratedF: fan.plenumRhtSensorState?.temperatureF,
    }));

    const controlSensors = dryingRunning ? [aux] : [aux, ...fans];
    const informationalSensors = dryingRunning ? fans : [];

    const renderLines = (sensors: SensorLine[], calibrated: boolean) => sensors.map((sensor) => (
        <div key={`${calibrated ? "cal" : "raw"}-${sensor.key}`}>
            <Text>
                <span style={{ fontWeight: "bold" }}>{sensor.label}:</span>{" "}
                {calibrated ? getCalibratedTempDisplay(sensor.uncalibratedF) : formatTemp(sensor.uncalibratedF)}
            </Text>
        </div>
    ));

    const renderGroup = (title: string, sensors: SensorLine[], calibrated: boolean) => {
        if (sensors.length === 0) return null;
        return (
            <div style={{ marginTop: 8 }}>
                <Text style={{ fontWeight: "bold" }}>{title}</Text>
                {renderLines(sensors, calibrated)}
            </div>
        );
    };

    return (
        <div style={{ maxWidth: 360 }}>
            <div>
                <Text style={{ fontWeight: "bold" }}>Uncalibrated</Text>
            </div>
            {renderGroup(dryingRunning ? "Control Sensor" : "Control Sensors", controlSensors, false)}
            {renderGroup("Informational sensors", informationalSensors, false)}

            <br />

            <div>
                <Text style={{ fontWeight: "bold" }}>Calibrated</Text>
            </div>
            {renderGroup(dryingRunning ? "Control Sensor" : "Control Sensors", controlSensors, true)}
            {renderGroup("Informational sensors", informationalSensors, true)}

            <br />

            <div>
                <Text>Average Uncalibrated Temperature: {formatTemp(avgUncalibratedF)}</Text>
            </div>
            <div>
                <Text>Calibration Offset: {formatOffset(calibrationF)}</Text>
            </div>
            <div>
                <Text>Average Calibrated Temperature: {formatTemp(avgCalibratedF)}</Text>
            </div>
        </div>
    );
};

export default PlenumTempInfoContent;
